import React from 'react';

import { showsUtilityRows } from '@shared/app-identity/appChrome';
import { useInstalledApp } from '@shared/app-identity/useInstalledApp';

import SideNavItem from './SideNavItem';

interface SideNavTopControlsProps {
  /** Visual collapsed flag (icon-only rail) from the parent's transition hook. */
  isCollapsed: boolean;
}

interface HomeRowProps {
  isCollapsed: boolean;
  /** Null in a browser tab, a slug inside an installed app. */
  installedApp: ReturnType<typeof useInstalledApp>;
}

/**
 * Home (browser tab only).
 *
 * Split out so the row's two gates (utility rows shown, browser tab) read as one decision.
 */
const HomeRow: React.FC<HomeRowProps> = ({ isCollapsed, installedApp }) => (
  <>
    {/* Home belongs to the root app. Inside an installed app it points outside
        that app's manifest scope, so the way back to Conway is the profile
        menu's "Open Conway" — one escape, in one place, for every area app,
        rather than a row that is the same destination wearing a different
        label. */}
    {installedApp === null && (
      <SideNavItem
        id="home"
        label="Home"
        icon="Home"
        domain="home"
        isCollapsed={isCollapsed}
        path="/home"
      />
    )}
  </>
);

/**
 * Home (browser tab only). The collapse toggle and Go box live in
 * `SideNavHeader`, pinned above this scroll column; the chat rows left for the
 * Chat destination and the Chat tab's sidebar.
 */
const SideNavTopControls: React.FC<SideNavTopControlsProps> = ({ isCollapsed }) => {
  const installedApp = useInstalledApp();
  // Apps with fewer than two destinations drop Home too — in Tasks the sidebar
  // was a Home icon standing over the empty space where a nav list would be.
  const showUtilityRows = showsUtilityRows(installedApp);

  return (
    <div className="px-2">
      {showUtilityRows && <HomeRow isCollapsed={isCollapsed} installedApp={installedApp} />}

      {/* Spacing before the nav groups — a quiet gap reads as separation
          without the heaviness of a divider line. */}
      <div className="h-3" aria-hidden="true" />
    </div>
  );
};

export default SideNavTopControls;
